import { PropsWithChildren } from 'react';
import { CampaignFilterOperator, ServiceCategory } from '@prisma/client';
import { Box, Typography } from '@mui/material';
import { CampaignFilter } from '../../../../types/marketing';
import SingleSelectControlled from '../../../form/base/SingleSelectControlled';
import TextInputControlled from '../../../form/base/TextInputControlled';
import { getBusinessCurrency } from '../../../../helpers/businesses';
import useUser from '../../../../hooks/global/useUser';
import { getFullName } from '../../../../helpers/global';
import { convertSnakeToTitleCase } from '../../../../utils/global/helper';

interface InputsProps {
  filter: CampaignFilter;
  onChange: (filter: Partial<CampaignFilter>) => void;
}

export const inputContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
};

function Text({ children: text }: PropsWithChildren) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: theme => theme.palette.darkGrey.main,
      }}
      whiteSpace="nowrap">
      {text}
    </Typography>
  );
}

export function NumberOperatorInput({ filter, onChange }: InputsProps) {
  return (
    <SingleSelectControlled
      id="operator"
      options={[
        { value: 'GT', label: 'is greater than' },
        { value: 'LT', label: 'is less than' },
      ]}
      onChange={value => {
        onChange({
          ...filter,
          operator: value as CampaignFilterOperator,
        });
      }}
      value={filter.operator}
      autoWidth={false}
    />
  );
}

export function NumberInput({ filter, onChange }: InputsProps) {
  return (
    <TextInputControlled
      id="value"
      placeholder="Enter a value"
      type="number"
      value={filter.value.toString()}
      onChange={value => {
        onChange({
          value,
        });
      }}
      min={0}
      step={1}
      inputSx={{
        minWidth: 120,
      }}
    />
  );
}

export function SpendInputs({ filter, onChange }: InputsProps) {
  const { user } = useUser();

  const currency = user?.business ? getBusinessCurrency(user.business) : 'USD';

  return (
    <Box {...inputContainerStyle}>
      <NumberOperatorInput filter={filter} onChange={onChange} />
      <NumberInput filter={filter} onChange={onChange} />
      <Text>{currency}</Text>
    </Box>
  );
}

export function DateInputs({ filter, onChange }: InputsProps) {
  const operatorValue =
    filter.category === 'LAST_APPOINTMENT_WITHIN_DAYS'
      ? `within-${filter.operator}`
      : filter.operator;
  return (
    <Box {...inputContainerStyle}>
      <SingleSelectControlled
        id="operator"
        options={[
          { value: 'within-GT', label: 'is within' },
          { value: 'within-LT', label: 'is not within' },
          { value: 'GT', label: 'is after' },
          { value: 'LT', label: 'is before' },
        ]}
        onChange={value => {
          const category = value.startsWith('within')
            ? 'LAST_APPOINTMENT_WITHIN_DAYS'
            : 'LAST_APPOINTMENT_DATE';
          const operator = value.replace(
            'within-',
            '',
          ) as CampaignFilterOperator;
          const isNewCategory = category !== filter.category;
          onChange({
            ...filter,
            category,
            operator,
            value: isNewCategory ? '' : filter.value,
          } as CampaignFilter);
        }}
        value={operatorValue}
        autoWidth={false}
      />
      {filter.category === 'LAST_APPOINTMENT_DATE' && (
        <TextInputControlled
          id="value"
          placeholder="Enter a date"
          type="date"
          value={(filter.value || '') as string}
          onChange={value => {
            onChange({
              value: value as string,
            });
          }}
        />
      )}
      {filter.category === 'LAST_APPOINTMENT_WITHIN_DAYS' && (
        <>
          <Text>the last</Text>
          <NumberInput filter={filter} onChange={onChange} />
          <Text>days</Text>
        </>
      )}
    </Box>
  );
}

export function CountInputs({ filter, onChange }: InputsProps) {
  return (
    <Box {...inputContainerStyle}>
      <NumberOperatorInput filter={filter} onChange={onChange} />
      <NumberInput filter={filter} onChange={onChange} />
      <Text>visits</Text>
    </Box>
  );
}

export function EmployeeSelectInput({ filter, onChange }: InputsProps) {
  const { user } = useUser();
  const employees = user?.business?.employees?.map(e => ({
    label:
      e.displayName?.trim() ||
      getFullName({
        firstName: e.user?.firstName || '',
        lastName: e.user?.lastName || '',
      }),
    value: e.employeeId,
  }));

  return (
    <Box {...inputContainerStyle}>
      <SingleSelectControlled
        id="value"
        options={employees ?? []}
        onChange={value => {
          onChange({ value });
        }}
        value={filter.value as string}
        autoWidth={false}
        placeholder="Select an employee"
        selectSx={{
          maxWidth: 250,
        }}
      />
    </Box>
  );
}

export function AppointmentServiceInput({ filter, onChange }: InputsProps) {
  const options = Object.values(ServiceCategory).map(service => ({
    label: convertSnakeToTitleCase(service),
    value: service,
  }));
  return (
    <Box {...inputContainerStyle}>
      <SingleSelectControlled
        id="value"
        options={options ?? []}
        onChange={value => {
          onChange({ value });
        }}
        value={filter.value as string}
        autoWidth={false}
        placeholder="Select a service"
        selectSx={{
          maxWidth: 250,
        }}
      />
    </Box>
  );
}
